import { supabase } from './supabase'

export interface ProfileLike {
  id: string
  profile_user_id: string
  user_id: string
  created_at: string
  user?: {
    id: string
    username: string
    first_name: string
    last_name: string
    avatar_url: string
  }
}

export const profilesService = {
  /**
   * Toggle like on a user profile
   */
  async toggleProfileLike(profileUserId: string, userId: string): Promise<boolean> {
    // Prevent self-liking
    if (profileUserId === userId) {
      throw new Error('Cannot like your own profile')
    }

    // Check if already liked
    const { data: existingLike } = await supabase
      .from('profile_likes')
      .select('id')
      .eq('profile_user_id', profileUserId)
      .eq('user_id', userId)
      .single()

    if (existingLike) {
      // Unlike
      const { error } = await supabase
        .from('profile_likes')
        .delete()
        .eq('profile_user_id', profileUserId)
        .eq('user_id', userId)

      if (error) throw error
      return false
    } else {
      // Like
      const { error } = await supabase
        .from('profile_likes')
        .insert({ profile_user_id: profileUserId, user_id: userId })

      if (error) throw error
      return true
    }
  },

  /**
   * Get profile likes count
   */
  async getProfileLikesCount(profileUserId: string): Promise<number> {
    const { count, error } = await supabase
      .from('profile_likes')
      .select('*', { count: 'exact', head: true })
      .eq('profile_user_id', profileUserId)

    if (error) throw error
    return count || 0
  },

  /**
   * Check if user has liked a profile
   */
  async hasUserLikedProfile(profileUserId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('profile_likes')
      .select('id')
      .eq('profile_user_id', profileUserId)
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return !!data
  },

  /**
   * Get users who liked a profile
   */
  async getProfileLikes(profileUserId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('profile_likes')
      .select(`
        id,
        profile_user_id,
        user_id,
        created_at,
        user:users!user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .eq('profile_user_id', profileUserId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  /**
   * Get profiles that a user has liked with stats
   */
  async getUserLikedProfiles(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('profile_likes')
      .select(`
        id,
        created_at,
        profile:users!profile_user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url,
          bio,
          location
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error

    // Type assertion: Supabase returns profile as single object, not array
    const profiles = (data as any)?.map((item: any) => item.profile).filter(Boolean) || []

    // Fetch stats for each profile
    const profilesWithStats = await Promise.all(
      profiles.map(async (profile: any) => {
        // Get contributions count
        const { count: contributionsCount } = await supabase
          .from('contributions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', profile.id)

        // Get curations count
        const { count: curationsCount } = await supabase
          .from('issues')
          .select('*', { count: 'exact', head: true })
          .eq('curator_id', profile.id)

        return {
          ...profile,
          contributions_count: contributionsCount || 0,
          curations_count: curationsCount || 0
        }
      })
    )

    return profilesWithStats
  }
}
